// this file don't follow MODULE_GUIDE.md

import {
  AiOutlineShop,
  AiOutlineUser,
  Hi2OutlineCurrencyDollar,
  HiOutlineLockOpen,
  IoMdHelpCircleOutline,
  MdLogout,
  TbDiamond,
  Wand2,
} from "@pkg/icons";
import type { ComponentType } from "react";

export interface NavItem {
  title: string;
  url: string;
  external?: boolean;
  badge?: string;
  /** When set, this tab navigates via `?tab=` search param instead of a URL */
  tab?: string;
}

/* ── Icon Registry ── */

export const profileMenuIcons: Record<
  string,
  ComponentType<{ className?: string }>
> = {
  user: AiOutlineUser,
  lock: HiOutlineLockOpen,
  dollar: Hi2OutlineCurrencyDollar,
  diamond: TbDiamond,
  wandSparkles: Wand2,
  shop: AiOutlineShop,
  helpCircle: IoMdHelpCircleOutline,
  logout: MdLogout,
};

/* ── Profile Menu Config ── */

export type ProfileMenuAction = "help" | "logout";

export interface ProfileMenuItemConfig {
  icon: string;
  text: string;
  /** Navigate via `?tab=` search param */
  tab?: string;
  /** Navigate to an internal app path */
  path?: string;
  /** External URL (opened in a new tab by CustomMenu) */
  url?: string;
  /** Special runtime action */
  action?: ProfileMenuAction;
  /** Condition under which the item is disabled */
  disableWhen?: "not-password-provider";
  hoverClass?: string;
}

export const EXTERNAL_LINKS = {
  selects: "https://aftershoot.com/selects/",
  edits: "https://aftershoot.com/edit/",
  retouch: "https://aftershoot.com/retouching/",
  marketplace: "https://marketplace.aftershoot.com",
  blog: "https://aftershoot.com/blog/",
  workshops: "https://aftershoot.com/live-workshops/",
  marketingGuide: "https://aftershoot.com/marketing-guide/",
  newsletter: "https://aftershoot.com/newsletter/",
  quiz: "https://aftershoot.com/quiz-for-photographers/",
  community: "https://www.facebook.com/groups/aftershootusers",
  home: "https://aftershoot.com",
  galleries: "https://aftershoot.com/galleries/",
} as const;

export const profileMenuItems: ProfileMenuItemConfig[] = [
  { icon: "user", text: "General", path: "/" },
  {
    icon: "lock",
    text: "Login and Security",
    path: "/pw-update",
    disableWhen: "not-password-provider",
  },
  { icon: "dollar", text: "Pricing", path: "/pricing" },
  { icon: "diamond", text: "Referral Program", path: "/referral" },
  { icon: "wandSparkles", text: "Retouch", url: EXTERNAL_LINKS.retouch },
  { icon: "shop", text: "Marketplace", url: EXTERNAL_LINKS.marketplace },
  { icon: "helpCircle", text: "Help", action: "help" },
  { icon: "logout", text: "Log out", action: "logout", hoverClass: "bg-tutu" },
];

// Photon context menu
export const photonProfileMenuItems: ProfileMenuItemConfig[] = [
  { icon: "logout", text: "Log out", action: "logout", hoverClass: "bg-tutu" },
];

/* ── Nav Link Registry ── */

// Each nav link is defined once here; the lists below only choose and order ids.
const NAV_LINKS = {
  general: { title: "General", url: "/" },
  referral: { title: "Referral", url: "/referral" },
  pricing: { title: "Pricing", url: "/pricing", badge: "25% OFF" },
  select: { title: "Select", url: EXTERNAL_LINKS.selects, external: true },
  edit: { title: "Edit", url: EXTERNAL_LINKS.edits, external: true },
  retouch: { title: "Retouch", url: EXTERNAL_LINKS.retouch, external: true },
  galleries: {
    title: "Galleries",
    url: EXTERNAL_LINKS.galleries,
    external: true,
  },
  marketplace: {
    title: "Marketplace",
    url: EXTERNAL_LINKS.marketplace,
    external: true,
  },
  blog: { title: "Blog", url: EXTERNAL_LINKS.blog, external: true },
} satisfies Record<string, NavItem>;

type NavLinkId = keyof typeof NAV_LINKS;

const navList = (...ids: NavLinkId[]): NavItem[] =>
  ids.map((id) => NAV_LINKS[id]);

export const loggedInTabs = navList(
  "general",
  "referral",
  "pricing",
  "retouch",
  "galleries",
  "marketplace",
);

export const loggedOutTabs = navList(
  "select",
  "edit",
  "retouch",
  "galleries",
  "pricing",
);

export const mobileNavItems = navList(
  "general",
  "select",
  "edit",
  "pricing",
  "marketplace",
  "retouch",
  "galleries",
  "blog",
);

export const resourcesDropdown: NavItem[] = [
  { title: "Blog", url: EXTERNAL_LINKS.blog, external: true },
  { title: "Workshops", url: EXTERNAL_LINKS.workshops, external: true },
  {
    title: "Marketing Guide",
    url: EXTERNAL_LINKS.marketingGuide,
    external: true,
  },
  {
    title: "Aftershoot Digest",
    url: EXTERNAL_LINKS.newsletter,
    external: true,
  },
  { title: "Photography Quiz", url: EXTERNAL_LINKS.quiz, external: true },
  {
    title: "Join Our Community",
    url: EXTERNAL_LINKS.community,
    external: true,
  },
];
